import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { connect } from 'react-redux';
import HttpService from '../../../services/httpService';
import { setLoading, setSnackInfo } from '../../../redux/actions/appAction';
import { getSourceOfLeadData } from '../../../redux/actions/leadSourceActions';

import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, FormControl, Box,
    Dialog, DialogContent, DialogActions,
    TextField, Select, MenuItem, InputLabel, Checkbox,
    Card, Typography, IconButton, CircularProgress
} from '@material-ui/core';

import { makeStyles, withStyles } from '@material-ui/core/styles';

// Icons
import FacebookIcon from "@material-ui/icons/Facebook";
import PublicIcon from "@material-ui/icons/Public";
import DirectionsWalkIcon from "@material-ui/icons/DirectionsWalk";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import CloseIcon from "@material-ui/icons/Close";
import FilterListIcon from "@material-ui/icons/FilterList";
import InboxIcon from "@material-ui/icons/Inbox";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
// Real, literal brand/concept icons instead of generic Material Design
// shapes (a plain globe for "Google", an office building for
// "Partnership", etc.) - much more recognizable at a glance.
import { FaHandshake, FaUserFriends, FaSitemap } from "react-icons/fa";

// The actual, official multi-color Google "G" mark (same asset used on
// "Sign in with Google" buttons everywhere) - there's no accurate
// single-color stand-in for this one, so it's hand-drawn as an inline SVG.
const GoogleGIcon = ({ style }) => (
    <svg viewBox="0 0 48 48" style={{ width: "1em", height: "1em", ...style }}>
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.9-2.26 5.36-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

// Brand orange - sampled from the actual Decorpot logo, same palette used
// across the rest of the app's recently-redesigned pages.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
// Matches the "Silka bold" heading treatment used on the lead details
// (manageProjectLog) page, so headings here read consistently with the rest
// of the app. Body text already inherits "Silka regular" globally via App.css.
const HEADING_FONT = "'Silka bold', 'Silka semiBold', sans-serif !important";

const useStyles = makeStyles(() => ({
    page: { padding: 24, background: "#f6f7fb", minHeight: "100vh" },
    headerCard: {
        marginBottom: 20,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(196,83,31,0.2)",
    },
    headerBanner: {
        background: HEADER_GRADIENT,
        padding: "24px 28px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 16,
    },
    headerLeft: { display: "flex", alignItems: "center", gap: 16 },
    headerIconWrap: {
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.22)",
        border: "2px solid rgba(255,255,255,0.35)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    headerTitle: {
        color: "#fff",
        fontFamily: HEADING_FONT,
        fontSize: 22,
        margin: 0,
    },
    headerSubtitle: {
        color: "rgba(255,255,255,0.88)",
        fontSize: 13.5,
        marginTop: 4,
    },
    statsRow: { display: "flex", gap: 10, flexWrap: "wrap" },
    statChip: {
        background: "rgba(255,255,255,0.2)",
        color: "#fff",
        borderRadius: 20,
        padding: "7px 16px",
        fontWeight: 700,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
    },
    filterBar: {
        padding: "16px 24px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        background: "#fff",
        borderBottom: "1px solid #f0f1f5",
    },
    filterLabel: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: "#6b7280",
        fontSize: 13,
        fontWeight: 600,
    },
    filterSelect: {
        minWidth: 200,
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
        '& .MuiInputLabel-root.Mui-focused': { color: ORANGE_DARK },
    },
    spacer: { flex: 1 },
    actionBtn: {
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 600,
        padding: "8px 18px",
        boxShadow: "none",
        transition: "all .15s ease",
    },
    refreshBtn: {
        color: ORANGE_DARK,
        border: `1.5px solid ${ORANGE}`,
        background: "#fff",
        '&:hover': { background: ORANGE_LIGHT, boxShadow: "0 4px 12px rgba(244,126,68,0.2)" },
    },
    addBtn: {
        color: "#fff",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
        '&:hover': { background: `linear-gradient(135deg, #a8441a, ${ORANGE_DARK})`, boxShadow: "0 6px 16px rgba(244,126,68,0.35)" },
        '&.Mui-disabled': { background: "rgba(0,0,0,0.12)", color: "rgba(0,0,0,0.38)" },
    },
    tableCard: { borderRadius: 16, overflow: "hidden", boxShadow: "0 6px 20px rgba(17,24,39,0.06)" },
    tableWrap: { maxHeight: 900, overflow: "auto" },
    sourceBadge: {
        transition: "transform .15s ease, box-shadow .15s ease",
        '&:hover': { transform: "scale(1.1)" },
    },
    sourceChip: {
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontWeight: 700,
        borderRadius: 20,
        padding: "4px 12px",
        fontSize: 12.5,
        display: "inline-block",
    },
    emptyState: {
        padding: "70px 20px",
        textAlign: "center",
        color: "#9aa1ac",
    },
    emptyIconWrap: {
        width: 84,
        height: 84,
        borderRadius: "50%",
        background: ORANGE_LIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 14px",
    },
    dialogHeader: {
        background: HEADER_GRADIENT,
        color: "#fff",
        padding: "18px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    dialogTitleText: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: HEADING_FONT,
        fontSize: 18,
    },
}));

const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: ORANGE_DARK,
        color: "#fff",
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: 0.2,
    }
}))(TableCell);

const StyledTableRow = withStyles(() => ({
    root: {
        '&:nth-of-type(odd)': { backgroundColor: "#fafafa" },
        '&:hover': { backgroundColor: ORANGE_LIGHT },
        transition: "background-color .12s ease",
    },
}))(TableRow);

// Custom-colored checkbox for the "Active" toggle - the default MUI primary
// (blue/indigo) clashed with the rest of this page's brand-orange theme.
const OrangeCheckbox = withStyles({
    root: {
        color: "#c7ccd6",
        '&$checked': { color: ORANGE },
    },
    checked: {},
})(Checkbox);

const SOURCE_TYPES = [
    "Facebook",
    "Google",
    "Partnership",
    "Reference",
    "Walk-In",
    "Channel Partner"
];

// Each source's own real-world/platform brand color (Facebook blue, Google
// red, etc.) - kept as identity colors rather than recolored to the app's
// orange theme. Every icon now sits in a soft tinted circle of its own
// color instead of a flat white/grey badge, so each source type reads as
// distinct and colorful at a glance.
const SOURCE_META = {
    "Facebook": { icon: FacebookIcon, color: "#1877F2", bg: "rgba(24,119,242,0.12)" },
    // Google has no single brand color, so the real 4-color "G" is rendered
    // on a plain white disc with a light border, matching how it actually
    // appears on "Sign in with Google" buttons everywhere.
    "Google": { icon: GoogleGIcon, color: undefined, bg: "#ffffff", border: "1px solid #e5e7eb" },
    "Partnership": { icon: FaHandshake, color: "#8a5a2b", bg: "rgba(138,90,43,0.12)" },
    "Reference": { icon: FaUserFriends, color: "#10B981", bg: "rgba(16,185,129,0.12)" },
    "Walk-In": { icon: DirectionsWalkIcon, color: "#F59E0B", bg: "rgba(245,158,11,0.14)" },
    "Channel Partner": { icon: FaSitemap, color: "#6366F1", bg: "rgba(99,102,241,0.12)" },
};
const DEFAULT_SOURCE_META = { icon: PublicIcon, color: "#9CA3AF", bg: "rgba(156,163,175,0.14)" };

// Reusable icon badge for a source type - used in the table, and in both
// the filter and "Add Source" dropdowns, so every place a source type
// appears looks the same and equally polished. `style.fontSize` (not a
// `size` prop) is used to size the icon itself, since that works uniformly
// across MUI SvgIcons, react-icons, and the hand-drawn GoogleGIcon SVG.
const SourceBadge = ({ type, size = 34, className }) => {
    const meta = SOURCE_META[type] || DEFAULT_SOURCE_META;
    const Icon = meta.icon;
    return (
        <div
            className={className}
            style={{
                width: size,
                height: size,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: meta.bg,
                border: meta.border,
                boxSizing: "border-box",
                flexShrink: 0,
            }}
        >
            <Icon style={{ color: meta.color, fontSize: size * 0.5 }} />
        </div>
    );
};

const ManageMarketingSource = (props) => {

    const classes = useStyles();
    const marketingSourceLists = useSelector((state) => state.leadSource.leadSource);

    const [filterType, setFilterType] = useState("ALL");
    const [openModal, setOpenModal] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState(null);

    const [formData, setFormData] = useState({
        name: "",
        sourceType: ""
    });

    const filteredSources = marketingSourceLists?.filter((row) => {
        if (filterType === "ALL") return true;
        return row?.sourceType === filterType;
    }) || [];

    const totalCount = marketingSourceLists?.length || 0;
    const activeCount = marketingSourceLists?.filter((row) => row?.isActive)?.length || 0;

    // Kept as a button-level spinner instead of the app-wide Backdrop
    // (props.setLoading) so refreshing this small list gives immediate,
    // visible feedback right where the click happened.
    const refreshData = () => {
        setRefreshing(true);
        props.getSourceOfLeadData();
        setTimeout(() => setRefreshing(false), 500);
    };

    const saveSource = async () => {
        if (!formData.name || !formData.sourceType) {
            props.setSnackInfo("Please fill all fields", "warning");
            return;
        }

        setSaving(true);
        try {
            const res = await HttpService.createMarketingSourceLead(formData);
            props.setSnackInfo(res.data.message, "success");
            setOpenModal(false);
            setFormData({ name: "", sourceType: "" });
            props.getSourceOfLeadData();
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || "Something went wrong", "error");
        }
        setSaving(false);
    };

    const changeStatus = async (id, e) => {
        setTogglingId(id);
        try {
            const res = await HttpService.changeSourceStatus(id, {
                isActive: e.target.checked
            });
            props.setSnackInfo(res.data.message, "success");
            props.getSourceOfLeadData();
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || "Something went wrong", "error");
        }
        setTogglingId(null);
    };

    const isFormValid = formData.name && formData.sourceType;

    return (
        <div className={classes.page}>
            <Card className={classes.headerCard}>
                <Box className={classes.headerBanner}>
                    <Box className={classes.headerLeft}>
                        <Box className={classes.headerIconWrap}>
                            <TrackChangesIcon style={{ color: "#fff", fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography className={classes.headerTitle}>
                                Marketing Sources
                            </Typography>
                            <Typography className={classes.headerSubtitle}>
                                Manage and track all lead sources efficiently
                            </Typography>
                        </Box>
                    </Box>

                    <Box className={classes.statsRow}>
                        <Box className={classes.statChip}>
                            <PublicIcon style={{ fontSize: 16 }} />
                            {totalCount} Total
                        </Box>
                        <Box className={classes.statChip}>
                            <CheckCircleIcon style={{ fontSize: 16 }} />
                            {activeCount} Active
                        </Box>
                    </Box>
                </Box>

                {/* FILTER BAR */}
                <Box className={classes.filterBar}>
                    <Box className={classes.filterLabel}>
                        <FilterListIcon style={{ fontSize: 17, color: ORANGE_DARK }} />
                        Filters
                    </Box>

                    <FormControl
                        variant="outlined"
                        size="small"
                        className={classes.filterSelect}
                    >
                        <InputLabel>Source Type</InputLabel>
                        <Select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            label="Source Type"
                        >
                            <MenuItem value="ALL">All</MenuItem>
                            {SOURCE_TYPES.map(type => (
                                <MenuItem key={type} value={type}>
                                    <Box style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <SourceBadge type={type} size={26} />
                                        {type}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <div className={classes.spacer} />

                    <Button
                        startIcon={refreshing ? <CircularProgress size={16} style={{ color: ORANGE_DARK }} /> : <RefreshIcon />}
                        onClick={refreshData}
                        disabled={refreshing}
                        variant="outlined"
                        className={`${classes.actionBtn} ${classes.refreshBtn}`}
                        style={{ cursor: refreshing ? "not-allowed" : "pointer" }}
                    >
                        {refreshing ? "Refreshing..." : "Refresh"}
                    </Button>

                    <Button
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => setOpenModal(true)}
                        variant="contained"
                        className={`${classes.actionBtn} ${classes.addBtn}`}
                        style={{ cursor: "pointer" }}
                    >
                        Add Source
                    </Button>
                </Box>
            </Card>

            {/* MODAL */}
            <Dialog
                open={openModal}
                onClose={() => !saving && setOpenModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ style: { borderRadius: 16, overflow: "hidden" } }}
            >
                <Box className={classes.dialogHeader}>
                    <Box className={classes.dialogTitleText}>
                        <AddCircleOutlineIcon />
                        Add Marketing Source
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => !saving && setOpenModal(false)}
                        style={{ color: "rgba(255,255,255,0.9)" }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <DialogContent style={{ paddingTop: 24 }}>
                    <TextField
                        fullWidth
                        label="Source Name"
                        value={formData.name}
                        onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                        }
                        variant="outlined"
                        style={{ marginBottom: 20 }}
                    />

                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Source Type</InputLabel>
                        <Select
                            value={formData.sourceType}
                            onChange={(e) =>
                                setFormData({ ...formData, sourceType: e.target.value })
                            }
                            label="Source Type"
                        >
                            {SOURCE_TYPES.map(type => (
                                <MenuItem key={type} value={type}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <SourceBadge type={type} size={28} />
                                        {type}
                                    </div>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>

                <DialogActions style={{ padding: "16px 24px" }}>
                    <Button onClick={() => setOpenModal(false)} disabled={saving} style={{ color: "#6b7280" }}>
                        Cancel
                    </Button>

                    <Button
                        onClick={saveSource}
                        variant="contained"
                        disabled={!isFormValid || saving}
                        className={classes.actionBtn}
                        startIcon={saving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
                        style={{
                            background: (!isFormValid || saving) ? "rgba(0,0,0,0.12)" : `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
                            color: (!isFormValid || saving) ? "rgba(0,0,0,0.38)" : "#fff",
                            cursor: (!isFormValid || saving) ? "not-allowed" : "pointer"
                        }}
                    >
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* TABLE */}
            <Card className={classes.tableCard}>
                <TableContainer className={classes.tableWrap}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>Sl. No.</StyledTableCell>
                                <StyledTableCell>Source Name</StyledTableCell>
                                <StyledTableCell>Source Type</StyledTableCell>
                                <StyledTableCell>Created By</StyledTableCell>
                                <StyledTableCell>Active</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>

                        <TableBody>
                            {filteredSources.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} style={{ border: "none" }}>
                                        <Box className={classes.emptyState}>
                                            <Box className={classes.emptyIconWrap}>
                                                <InboxIcon style={{ fontSize: 38, color: ORANGE_DARK }} />
                                            </Box>
                                            <Typography style={{ fontWeight: 600, color: "#7d8794" }}>
                                                No marketing sources found
                                            </Typography>
                                            <Typography variant="body2" style={{ color: "#a4abb5", marginTop: 4 }}>
                                                {filterType === "ALL"
                                                    ? "Click \"Add Source\" to create one."
                                                    : "Try a different Source Type filter."}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredSources.map((row, i) => (
                                    <StyledTableRow key={row._id}>
                                        <StyledTableCell>{i + 1}</StyledTableCell>
                                        <StyledTableCell style={{ fontWeight: 600 }}>{row?.name}</StyledTableCell>

                                        <StyledTableCell>
                                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                <SourceBadge type={row?.sourceType} size={36} className={classes.sourceBadge} />
                                                <span className={classes.sourceChip}>
                                                    {row?.sourceType || "NA"}
                                                </span>
                                            </div>
                                        </StyledTableCell>

                                        <StyledTableCell>
                                            {row?.createdBy?.name || "NA"}
                                        </StyledTableCell>

                                        <StyledTableCell>
                                            {togglingId === row._id ? (
                                                <CircularProgress size={20} style={{ color: ORANGE }} />
                                            ) : (
                                                <OrangeCheckbox
                                                    checked={!!row?.isActive}
                                                    onChange={(e) => changeStatus(row._id, e)}
                                                />
                                            )}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </div>
    );
};

export default connect(
    null,
    { setLoading, setSnackInfo, getSourceOfLeadData }
)(ManageMarketingSource);
